<script setup lang="ts">
import type { EventDuplicateForm } from '@/types/event';
import type { Timezone } from '@/types/country-timezone';
import useVuelidate from '@vuelidate/core';
import { helpers, maxValue, required } from '@vuelidate/validators';
import type { DropdownOption } from '@/types/dropdown';
import dayjs from 'dayjs';

const props = defineProps({
	isShow: {
		type: Boolean,
		default: false,
		required: true,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	timezone: {
		type: String,
		default: DEFAULT_TIMEZONE,
		required: true,
	},
});

const emit = defineEmits<{
	(e: 'onClose'): void
	(e: 'onConfirm', data: EventDuplicateForm): void
}>();

const defaultForm: EventDuplicateForm = {
	startDate: '',
	startTime: '',
	startDateTime: '',
	endDate: '',
	endTime: '',
	endDateTime: '',
	timezone: '',
	eventFrequency: EVENT_RECURRING_TYPE.ONCE,
	eventMonthlyOption: '',
	eventCount: '',
};

const eventOccurOptions: DropdownOption[] = [{
	name: mappedEventRecurringOption[EVENT_RECURRING_TYPE.ONCE],
	value: EVENT_RECURRING_TYPE.ONCE,
},
{
	name: mappedEventRecurringOption[EVENT_RECURRING_TYPE.DAILY],
	value: EVENT_RECURRING_TYPE.DAILY,
},
{
	name: mappedEventRecurringOption[EVENT_RECURRING_TYPE.WEEKLY],
	value: EVENT_RECURRING_TYPE.WEEKLY,
},
{
	name: mappedEventRecurringOption[EVENT_RECURRING_TYPE.BI_WEEKLY],
	value: EVENT_RECURRING_TYPE.BI_WEEKLY,
},
{
	name: mappedEventRecurringOption[EVENT_RECURRING_TYPE.MONTHLY],
	value: EVENT_RECURRING_TYPE.MONTHLY,
},
{
	name: mappedEventRecurringOption[EVENT_RECURRING_TYPE.ANNUALLY],
	value: EVENT_RECURRING_TYPE.ANNUALLY,
}];

const profileStore = useProfileStore();
const profileCountryId = computed(() => profileStore.profile?.countryId || '');
const form = reactive<EventDuplicateForm>(deepClone(defaultForm));
const selectedStartDateTime = computed(() => convertDateAndTimeToUtc(form.startDate, form.startTime));
const { timezones } = useTimezoneList(selectedStartDateTime, profileCountryId);

const rules = computed(() => {
	const dynamicRules = {
		startDate: {
			required: helpers.withMessage('Please enter your event schedule.', (value: string) => {
				return !!value;
			}),
		},
		startTime: {
			isValidTime: helpers.withMessage(
				'Date and time must be set in the future.',
				(value: string) => {
					return isValidTime(value) && isFutureTime(new Date(), form.startDate, value, form.timezone);
				},
			),
		},
		endDate: {
			required: helpers.withMessage('Please enter your event schedule.', (value: string) => {
				return !!value;
			}),
		},
		endTime: {
			isValidTime: helpers.withMessage(
				'Date and time must be set in the future.',
				(value: string) => {
					return isValidTime(value) && isFutureTime(new Date(), form.endDate, value, form.timezone);
				},
			),
		},
		endDateTime: {
			isAfterStartDate: helpers.withMessage(
				'End date and time must be after start date and time.',
				(value: string) => {
					return dayjs(value).isAfter(form.startDateTime);
				},
			),
		},
	};

	if (form.eventFrequency !== EVENT_RECURRING_TYPE.ONCE) {
		const newDynamicRules = {
			...dynamicRules,
			eventCount: {
				required: helpers.withMessage('Please enter the number of events in this series.', required),
			},
		};

		switch (form.eventFrequency) {
			case EVENT_RECURRING_TYPE.DAILY:
				return {
					...newDynamicRules,
					eventCount: {
						...newDynamicRules.eventCount,
						maxValue: helpers.withMessage(`The maximum number of events for a daily series is ${MAX_EVENT_RECURRING_DAILY}.`, maxValue(MAX_EVENT_RECURRING_DAILY)),
					},
				};
			case EVENT_RECURRING_TYPE.WEEKLY:
				return {
					...newDynamicRules,
					eventCount: {
						...newDynamicRules.eventCount,
						maxValue: helpers.withMessage(`The maximum number of events for a weekly series is ${MAX_EVENT_RECURRING_WEEKLY}.`, maxValue(MAX_EVENT_RECURRING_WEEKLY)),
					},
				};
			case EVENT_RECURRING_TYPE.BI_WEEKLY:
				return {
					...newDynamicRules,
					eventCount: {
						...newDynamicRules.eventCount,
						maxValue: helpers.withMessage(`The maximum number of events for a bi-weekly series is ${MAX_EVENT_RECURRING_BI_WEEKLY}.`, maxValue(MAX_EVENT_RECURRING_BI_WEEKLY)),
					},
				};
			case EVENT_RECURRING_TYPE.MONTHLY:
				return {
					...newDynamicRules,
					eventCount: {
						...newDynamicRules.eventCount,
						maxValue: helpers.withMessage(`The maximum number of events for a monthly series is ${MAX_EVENT_RECURRING_MONTHLY}.`, maxValue(MAX_EVENT_RECURRING_MONTHLY)),
					},
					eventMonthlyOption: {
						required: helpers.withMessage('Please select an option.', required),
					},
				};
			case EVENT_RECURRING_TYPE.ANNUALLY:
				return {
					...newDynamicRules,
					eventCount: {
						...newDynamicRules.eventCount,
						maxValue: helpers.withMessage(`The maximum number of events for a annually series is ${MAX_EVENT_RECURRING_ANNUALLY}.`, maxValue(MAX_EVENT_RECURRING_ANNUALLY)),
					},
				};
			default:
				break;
		}
	}
	return dynamicRules;
});

const v$ = useVuelidate(rules, form);

const startDateTimeErrorMessage = computed(() => {
	const dateErrorMessage = getValidationErrorMessage(v$.value.startDate.$errors);
	if (dateErrorMessage) {
		return dateErrorMessage;
	}

	const timeErrorMessage = getValidationErrorMessage(v$.value.startTime.$errors);

	if (timeErrorMessage) {
		return timeErrorMessage;
	}

	return '';
});

const endDateTimeErrorMessage = computed(() => {
	const dateErrorMessage = getValidationErrorMessage(v$.value.endDate.$errors);
	if (dateErrorMessage) {
		return dateErrorMessage;
	}

	const timeErrorMessage = getValidationErrorMessage(v$.value.endDateTime.$errors);

	if (timeErrorMessage) {
		return timeErrorMessage;
	}

	const dateTimeErrorMessage = getValidationErrorMessage(v$.value.endDateTime.$errors);

	if (dateTimeErrorMessage) {
		return dateTimeErrorMessage;
	}

	return '';
});

const isShowEventSeriesCount = computed(() => {
	return form.eventFrequency !== EVENT_RECURRING_TYPE.ONCE;
});

const isRequiredStartDateForMonthly = computed(() => {
	return !form.startDateTime && form.eventFrequency === EVENT_RECURRING_TYPE.MONTHLY;
});

const isShowMonthlyOptions = computed(() => {
	return form.startDateTime && form.eventFrequency === EVENT_RECURRING_TYPE.MONTHLY;
});

const eventMonthlyOptionErrorMessage = computed(() => {
	return getValidationErrorMessage(v$.value.eventMonthlyOption.$errors);
});

const eventMonthlyOnDateLabel = computed(() => {
	const day = convertDateTimeWithStrictTimezone(form.startDate, form.timezone, 'D');
	return `Monthly on day ${day}`;
});

const eventMonthlyOnDayOfWeekLabel = computed(() => {
	const dayOfWeeks = getDayOfWeeks(form.startDate, form.timezone);
	return `Monthly on the ${dayOfWeeks}`;
});

function handleStartDateChange(date: string) {
	form.startDate = date;
	form.startDateTime = convertDateAndTimeToUtc(date, form.startTime);
}

function handleStartTimeChange(time: string) {
	form.startTime = time;
	form.startDateTime = convertDateAndTimeToUtc(form.startDate, time);
}

function handleEndDateChange(date: string) {
	form.endDate = date;
	form.endDateTime = convertDateAndTimeToUtc(date, form.endTime);
}

function handleEndTimeChange(time: string) {
	form.endTime = time;
	form.endDateTime = convertDateAndTimeToUtc(form.endDate, time);
}

function handleDateChange(type: DATE_TYPE, date: string | string[]) {
	if (type === DATE_TYPE.START_DATE && typeof date === 'string') {
		handleStartDateChange(date);
	} else if (type === DATE_TYPE.END_DATE && typeof date === 'string') {
		handleEndDateChange(date);
	}
}

function handleSelectTimezone(data: Timezone) {
	form.timezone = data.timezone;
}

function handleClose() {
	emit('onClose');
}

function handleConfirmDuplicate() {
	v$.value.$touch();

	if (v$.value.$error) {
		return;
	}
	emit('onConfirm', form);
}

watch(
	() => props.isShow,
	(isShow) => {
		if (isShow) {
			Object.assign(form, deepClone(defaultForm));
			form.timezone = props.timezone;
		} else {
			v$.value.$reset();
		}
	},
);
</script>
<template>
  <ConfirmDialog
    class="event-series-dialog"
    :is-show="isShow"
    :is-loading="isLoading"
    :persistent="isLoading"
    confirm-button-name="Draft event series"
    @on-close="handleClose"
    @on-confirm="handleConfirmDuplicate"
  >
    <template #header>
      <div class="icon-header-confirm-dlg icon-info">
        <InfoCircleIcon
          width="24"
          height="24"
          :color="ICON_COLOR.PRIMARY"
        />
      </div>
    </template>
    <template #title>
      Draft an event series
    </template>
    <template #content>
      <div class="support-text text-sm text-regular">
        <p>
          Would you like to draft a new event series? A new draft event will be generated with the same details as the original event. Please select the date, time and frequency for the upcoming events in the series.
        </p>
      </div>
      <div class="form-container">
        <div>
          <label class="label text-sm text-medium"> Start date & time </label>
          <BaseDateTimePicker
            id="duplicateStartDate"
            time-drop-direction="up"
            :disabled="isLoading"
            :date="form.startDate"
            :time="form.startTime"
            :timezone="form.timezone"
            :date-errors="v$.startDate.$errors"
            :time-errors="v$.startTime.$errors"
            is-teleport-date-picker
            @on-date-change="(date) => handleDateChange(DATE_TYPE.START_DATE, date)"
            @on-time-change="handleStartTimeChange"
          />
          <p
            v-if="!!startDateTimeErrorMessage"
            class="error-message"
          >
            {{ startDateTimeErrorMessage }}
          </p>
        </div>
        <div>
          <label class="label text-sm text-medium"> End date & time </label>
          <BaseDateTimePicker
            id="duplicateEndDate"
            time-drop-direction="up"
            :disabled="isLoading"
            :date="form.endDate"
            :time="form.endTime"
            :timezone="form.timezone"
            :date-errors="v$.endDate.$errors || v$.endDateTime.$errors"
            :time-errors="v$.endTime.$errors || v$.endDateTime.$errors"
            is-teleport-date-picker
            @on-date-change="(date) => handleDateChange(DATE_TYPE.END_DATE, date)"
            @on-time-change="handleEndTimeChange"
          />
          <p
            v-if="!!endDateTimeErrorMessage"
            class="error-message"
          >
            {{ endDateTimeErrorMessage }}
          </p>
        </div>
        <div>
          <BaseTimezonePicker
            :disabled="isLoading"
            :selected-timezone="form.timezone"
            :timezone-options="timezones"
            @on-select-timezone="handleSelectTimezone"
          />
        </div>
        <div>
          <label class="label text-sm text-medium">How often do the events occur?</label>
          <BaseDropdown
            v-model="form.eventFrequency"
            :items="eventOccurOptions"
            :value="form.eventFrequency"
            :disabled="isLoading"
          />
          <p
            v-if="isRequiredStartDateForMonthly"
            class="error-message"
          >
            Please select a start date for the events that occur.
          </p>

          <div
            v-if="isShowMonthlyOptions"
            class="monthly-option-group"
          >
            <BaseRadio
              :id="EVENT_RECURRING_TYPE.MONTHLY"
              v-model="form.eventMonthlyOption"
              class="monthly-option"
              :label="eventMonthlyOnDateLabel"
              :value="EVENT_RECURRING_TYPE.MONTHLY"
              :disabled="isLoading"
            />
            <BaseRadio
              :id="EVENT_RECURRING_TYPE.MONTHLY_WEEKDAY"
              v-model="form.eventMonthlyOption"
              class="monthly-option"
              :label="eventMonthlyOnDayOfWeekLabel"
              :value="EVENT_RECURRING_TYPE.MONTHLY_WEEKDAY"
              :disabled="isLoading"
            />
            <p
              v-if="eventMonthlyOptionErrorMessage"
              class="error-message"
            >
              {{ eventMonthlyOptionErrorMessage }}
            </p>
          </div>
        </div>
        <div v-if="isShowEventSeriesCount">
          <BaseTextInput
            v-model.integer="form.eventCount"
            class="input-event-count"
            label="How many events will be in this series?"
            :disabled="isLoading"
            :error-message="getValidationErrorMessage(v$.eventCount.$errors)"
          />
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<style scoped lang="scss">
.support-text {
    display: grid;
    gap: spacings-get(3);
    margin-bottom: spacings-get(3);
}

label,
:deep(.input-event-count.input-container label) {
    color: colors-get(gray, 700);
}

.form-container,
.monthly-option-group {
    display: grid;
    gap: spacings-get(3);
	margin-top: spacings-get(3);
}

.monthly-option-group {
	.monthly-option :deep(.label) {
		@include fonts-weight(medium);
	}

	.error-message {
		margin-top: 0;
	}
}

:deep(.vue__time-picker) {
	position: fixed;
	// bottom: 0; // TODO: Set position to the middle of parent
    left: calc(100% - rem(24));
    z-index: 1;
}
</style>